import React from "react";
import { XCircleIcon, CheckCircle2Icon } from "lucide-react";
import { CTAButton } from "../CTAButton";
import { afterItems, beforeItems, planningSteps } from "../../data/content";
export function Transformation() {
  return <section className="w-full bg-navy px-4 py-16 sm:py-20" data-id="element-300" data-mp="1nqakx1zmbi4h">
      <div className="mx-auto max-w-5xl" data-id="element-301" data-mp="1iqxuy8dg9uxo">
        <p className="text-center font-display text-sm font-extrabold uppercase tracking-wider text-brand" data-id="element-302" data-mp="1irz8cjhxutik">The measurable transformation</p>
        <h2 className="mt-3 text-center font-display text-[26px] font-black leading-tight text-white sm:text-4xl" data-id="element-303" data-mp="2bf21ew3uic1f">
          Organize Your Next Post-Endodontic Restorative Plan in Approximately <span className="text-cta" data-id="element-304" data-mp="26m0kkik2w2f7">15 Minutes.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-center text-[17px] text-mist" data-id="element-305" data-mp="21oqztg7mwexp">
          Instead of starting from a blank page, use a structured worksheet to organize your assessment. You’ll follow a repeatable sequence:
        </p>

        <ol className="mx-auto mt-10 grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-306" data-mp="2uc3df0vthcev">
          {planningSteps.map((s, i) => <li key={s.label} className="flex gap-4 rounded-2xl border border-line bg-panel p-5" data-id="element-307" data-mp="1a7izkh3waqcj" data-mpi={i}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand font-display text-lg font-black text-ink" data-id="element-308" data-mp="wgo01vfadllm">
                {i + 1}
              </span>
              <div data-id="element-309" data-mp="mh5h9ib445v5">
                <p className="font-display text-lg font-black uppercase text-white" data-id="element-310" data-mp="mgnfy79lf1gd">{s.label}</p>
                <p className="mt-1 text-[15px] leading-snug text-mist" data-id="element-311" data-mp="rht3zrsb60jk">{s.text}</p>
              </div>
            </li>)}
        </ol>

        <p className="mx-auto mt-8 max-w-2xl text-center text-[15px] text-mist" data-id="element-312" data-mp="djd0satnms66">
          <span className="font-bold text-white" data-id="element-313" data-mp="hivk525tuv6w">The goal isn’t to replace clinical judgment or patient-specific diagnosis.</span> The goal is to give your clinical reasoning a practical structure.
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-center text-xs text-mist/70" data-id="element-314" data-mp="2buip3hifoc10">
          The 15-minute target refers to organizing the initial planning worksheet for a suitable case, not completing a comprehensive diagnosis or guaranteeing a clinical outcome.
        </p>

        <h3 className="mt-16 text-center font-display text-[24px] font-black leading-tight text-white sm:text-3xl" data-id="element-315" data-mp="3btoi74jc6tqm">
          What Changes When You Use a Structured Restorative Blueprint?
        </h3>

        <div className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-316" data-mp="7e5f2nf4nayg">
          <div className="overflow-hidden rounded-3xl border-2 border-alert/70 bg-[#2a0d16]" data-id="element-317" data-mp="3cjush09jue8">
            <div className="flex items-center gap-3 bg-alert px-5 py-4" data-id="element-318" data-mp="3urhjk2ebavlw">
              <XCircleIcon className="h-7 w-7 text-white" aria-hidden data-id="element-319" data-mp="3upej0ofj9k2x" />
              <div data-id="element-320" data-mp="3ps4vjebpfihq">
                <p className="font-display text-2xl font-black uppercase text-white" data-id="element-321" data-mp="3pqk1dptay1ke">Before</p>
                <p className="text-sm font-semibold text-white/90" data-id="element-322" data-mp="r4qxdxwl0xm3">Planning without a structured framework</p>
              </div>
            </div>
            <ul className="space-y-3.5 p-5" data-id="element-323" data-mp="tnm1gguvg0gt">
              {beforeItems.map(t => <li key={t} className="flex gap-3" data-id="element-324" data-mp="ylxawqh988yi">
                  <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-alert" aria-hidden data-id="element-325" data-mp="ymfds5yf9lfe" />
                  <span className="text-[15px] leading-snug text-white/90" data-id="element-326" data-mp="13jp0hduffizj">{t}</span>
                </li>)}
            </ul>
          </div>

          <div className="overflow-hidden rounded-3xl border-2 border-ok/70 bg-[#0a2a1d]" data-id="element-327" data-mp="1cm82jvqe1rkq">
            <div className="flex items-center gap-3 bg-ok px-5 py-4" data-id="element-328" data-mp="1hkrmjqlf8tag">
              <CheckCircle2Icon className="h-7 w-7 text-ink" aria-hidden data-id="element-329" data-mp="1hiyd63jfbuyr" />
              <div data-id="element-330" data-mp="1mc9oohz6kzx0">
                <p className="font-display text-2xl font-black uppercase text-ink" data-id="element-331" data-mp="1mduiu6hl2guc">After</p>
                <p className="text-sm font-semibold text-ink/80" data-id="element-332" data-mp="1ra1e95ccs9y1">With Post-Endodontic Restoration Blueprint™</p>
              </div>
            </div>
            <ul className="space-y-3.5 p-5" data-id="element-333" data-mp="20aznc21nf7nh">
              {afterItems.map(t => <li key={t} className="flex gap-3" data-id="element-334" data-mp="1mok2i0vzd31k">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-ok" aria-hidden data-id="element-335" data-mp="1mn90o6m3i5d3" />
                  <span className="text-[15px] leading-snug text-white" data-id="element-336" data-mp="1imp3x45fp3rh">{t}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12" data-id="element-337" data-mp="1rf0i9rn9x0r3">
          <CTAButton label="I want to plan my restorations with greater clarity" data-id="element-338" data-mp="1rgtrnep9tz2s" />
        </div>
      </div>
    </section>;
}